import { useEffect, useMemo, useState } from "react";
import { useOffice } from "@/core/hooks/useOffice";
import { useProgress } from "@/core/hooks/useProgress";
import { coursesForHost } from "@/courses/registry";
import { flattenLessons, type Course } from "@/core/types";
import { CourseOverview } from "@/ui/components/CourseOverview";
import { HostNotice } from "@/ui/components/HostNotice";
import { BrandMark, Icon } from "@/ui/components/Icon";
import { LessonView } from "@/ui/components/LessonView";
import { WebApp } from "@/ui/web/WebApp";

/** `?panel` in der Adresse zeigt im Browser das Seitenpanel statt der Website. */
const PANEL_VORSCHAU = new URLSearchParams(window.location.search).has("panel");

type View = { type: "overview" } | { type: "lesson"; moduleId: string; lessonId: string };

/** Platzhalterkurs, solange für die laufende Anwendung keiner passt. */
const KEIN_KURS: Course = {
  id: "leer",
  host: "Word",
  title: "",
  summary: "",
  level: "Einsteiger",
  modules: [],
};

export function App() {
  const office = useOffice();
  const [view, setView] = useState<View>({ type: "overview" });
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  // Welche Kurse passen zur laufenden Office-Anwendung? Läuft das Panel im
  // normalen Browser, ist `host` null und es stehen alle Kurse zur Auswahl.
  const available = useMemo(() => coursesForHost(office.host), [office.host]);

  const course = useMemo(() => {
    const gewaehlt = available.find((entry) => entry.id === selectedCourseId);
    return gewaehlt ?? available[0] ?? null;
  }, [available, selectedCourseId]);

  const progress = useProgress(course ?? KEIN_KURS);

  // Jede Ansicht beginnt oben – auch wenn die vorige weit gescrollt war.
  const viewKey = view.type === "lesson" ? `${view.moduleId}/${view.lessonId}` : "overview";
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [viewKey]);

  if (!office.ready) {
    return (
      <div className="app" aria-busy="true">
        <header className="topbar">
          <span className="brand">
            <BrandMark />
            <span className="brand__name">Interaktive Office-Kurse</span>
          </span>
        </header>
        <main className="canvas loading" aria-label="Kurs wird geladen">
          <span className="skeleton skeleton--title" />
          <span className="skeleton skeleton--line" />
          <span className="skeleton skeleton--line skeleton--short" />
          <span className="skeleton skeleton--panel" />
        </main>
      </div>
    );
  }

  // Im normalen Browser erscheint die Website mit allen Kursen. Mit `?panel`
  // in der Adresse lässt sich dort weiter das Seitenpanel ansehen.
  if (office.host === null && !PANEL_VORSCHAU) {
    return <WebApp />;
  }

  if (!course) {
    return (
      <div className="app">
        <header className="topbar">
          <span className="brand">
            <BrandMark />
            <span className="brand__name">Interaktive Office-Kurse</span>
          </span>
        </header>
        <main className="canvas">
          <p className="empty">Für diese Office-Anwendung ist noch kein Kurs verfügbar.</p>
        </main>
      </div>
    );
  }

  const lessons = flattenLessons(course);

  const openLesson = (moduleId: string, lessonId: string) =>
    setView({ type: "lesson", moduleId, lessonId });

  const wechsleKurs = (courseId: string) => {
    setSelectedCourseId(courseId);
    setView({ type: "overview" });
  };

  let content = (
    <CourseOverview
      key={course.id}
      course={course}
      progress={progress}
      onOpenLesson={openLesson}
    />
  );
  let inLesson = false;

  if (view.type === "lesson") {
    const currentIndex = lessons.findIndex(
      (entry) => entry.module.id === view.moduleId && entry.lesson.id === view.lessonId,
    );
    const current = lessons[currentIndex];

    if (current) {
      inLesson = true;
      const next = lessons[currentIndex + 1];
      content = (
        <LessonView
          module={current.module}
          lesson={current.lesson}
          progress={progress}
          onBack={() => setView({ type: "overview" })}
          onNextLesson={next ? () => openLesson(next.module.id, next.lesson.id) : undefined}
          nextLessonTitle={next?.lesson.title}
        />
      );
    }
  }

  return (
    <div className="app" data-host={course.host}>
      <header className="topbar">
        {inLesson ? (
          <button
            type="button"
            className="topbar__back"
            onClick={() => setView({ type: "overview" })}
          >
            <Icon name="chevronLeft" />
            Kursübersicht
          </button>
        ) : (
          <span className="brand">
            <BrandMark />
            <span className="brand__name">Interaktive Office-Kurse</span>
          </span>
        )}
        {inLesson ? <BrandMark /> : null}
      </header>

      {!inLesson && available.length > 1 ? (
        <nav className="course-switch" aria-label="Kurs auswählen">
          {available.map((entry) => (
            <button
              key={entry.id}
              type="button"
              data-host={entry.host}
              aria-pressed={entry.id === course.id}
              onClick={() => wechsleKurs(entry.id)}
            >
              {entry.host}
            </button>
          ))}
        </nav>
      ) : null}

      <HostNotice expectedHost={course.host} actualHost={office.host} />

      <main className="canvas">
        <div className={`view ${inLesson ? "view--lesson" : "view--overview"}`} key={viewKey}>
          {content}
        </div>
      </main>
    </div>
  );
}
